<template>
  <div>
    <template v-if="isLoading">
      <p class="text-h6 is-4">
        Loading...
        <i class="fa fa-refresh fa-spin fa-fw" />
      </p>
    </template>
    <template v-else>
      <template v-if="pluginUpdateLogs.length">
        <div style="min-height: 530px">
          <table class="table">
            <thead>
              <tr>
                <th class="date">Date</th>
                <th>Plugin</th>
                <th>User</th>
                <th>Action</th>
                <th>From</th>
                <th>To</th>
                <th>Success</th>
                <th />
              </tr>
            </thead>
            <tbody>
              <tr v-for="log in pluginUpdateLogs" :key="`log_${log._id}`">
                <td>{{ log.createdAt | dateFormat }}</td>
                <td>
                  <router-link :to="{ name: 'pluginDetails', params: { pluginKey: log.pluginId } }">
                    <span v-html="log.pluginName" />
                  </router-link>
                </td>
                <td>{{ log.userFirstName }} {{ log.userLastName }}</td>
                <td>{{ log.action }}</td>
                <td>{{ log.currentVersion }}</td>
                <td>{{ log.newVersion }}</td>
                <td>{{ log.success ? 'Yes' : log.paidPlugin ? 'Plugin requires licensing' : 'No' }}</td>
                <td>
                  <c-button-group class="float-right">
                    <c-button color="primary" size="sm" v-if="log.s3BackupKey" @click="downloadZipBackup(log._id)">
                      <c-icon :content="freeSet.cilCloudDownload" />
                    </c-button>
                    <c-button
                      v-if="log.action === 'update'"
                      color="plugin-undo"
                      size="sm"
                      :disabled="isRollingBack"
                      class="btn btn-warning pull-right rollback-button"
                      :class="{ 'is-loading': log.isLoading }"
                      @click="checkRollback(log)"
                    >
                      <span>Undo</span>
                    </c-button>
                  </c-button-group>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <paginator
          :page-list="pages.pageList"
          :current-page="Number(pages.currentPage)"
          :num-pages="Number(pages.numPages)"
          @loadPage="get"
        />
        <confirm-modal
          title="Undo plugin update"
          ok-text="Ok"
          :show.sync="showRollbackModal"
          @update:confirm="confirmRollback"
        >
          <div v-html="rollbackMessage" />
        </confirm-modal>
      </template>
      <p v-else class="p-3 h6">
        <span style="display: block">
          You haven't given this site any love yet. Go ahead and update a plugin now (scroll up). There’s no time like
          the present...
        </span>
        <random-gif />
      </p>
    </template>
  </div>
</template>

<script>
import { freeSet } from '@coreui/icons';
import { buildEndpoint } from '@/libs/buildEndPoint';
import Paginator from './Paginator';
import ConfirmModal from '@/views/components/ConfirmModal';
import RandomGif from './random-gif';
import { errorMessage } from '@/libs/ui/message';

export default {
  components: {
    Paginator,
    ConfirmModal,
    RandomGif,
  },

  props: {
    site: Object,
    doRefresh: Number,
  },
  data() {
    return {
      error: null,
      pluginUpdateLogs: [],
      isLoading: false,
      pages: {},
      showRollbackModal: false,
      rollbackToPlugin: null,
      rollbackMessage: '',
      isRollingBack: false,
      freeSet,
    };
  },
  created() {
    this.get();
  },
  mounted() {
    this.$watch(
      'doRefresh',
      () => {
        this.get();
        this.rollbackToPlugin = null;
        this.isRollingBack = false;
      },
      { immediate: true },
    );
  },
  methods: {
    checkRollback(log) {
      this.rollbackToPlugin = log;
      this.rollbackMessage = `<strong>${
        log.pluginName
      }</strong> will be ${
        log.action === 'update' ? 'downgraded' : 'updated'
      } to version `
        + `<strong>${
          log.currentVersion
        }</strong>?`;
      this.showRollbackModal = true;
    },
    cancelRollback() {
      this.showRollbackModal = false;
    },
    confirmRollback() {
      this.rollbackToPlugin.isLoading = true;
      this.isRollingBack = true;
      this.showRollbackModal = false;
      this.$emit('rollbackPlugin', this.rollbackToPlugin);
    },
    get(pageNum) {
      this.isLoading = true;
      const { _id: siteId } = this.site;
      this.$http
        .get(`${buildEndpoint('plugin-update-logs/') + siteId}?page=${pageNum || 1}`)
        .then((response) => {
          this.isLoading = false;
          if (response.data.success) {
            const { data } = response.data;
            this.pluginUpdateLogs = data.docs;
            const pageList = [];
            for (let i = 1; i <= data.pages; i++) {
              if (i > data.page - 5 && i < data.page + 5) {
                pageList.push(i);
              }
            }
            this.pages = {
              numPages: data.pages,
              currentPage: data.page,
              totalDocs: data.total,
              pageList,
            };
          } else {
            errorMessage(this.$store, { message: 'Error getting log data' });
          }
        })
        .catch((error) => {
          errorMessage(this.$store, { message: error.message });
        });
    },
    downloadZipBackup(logId) {
      const { _id: siteId } = this.site;
      this.$http
        .get(buildEndpoint(`plugin/download-zip-backup/${siteId}/${logId}`))
        .then((response) => {
          if (response.data.success) {
            window.open(response.data.data.url, '_blank');
          } else {
            this.error = 'Error getting log data';
            errorMessage(this.$store, { message: response.data.message });
          }
        })
        .catch((error) => {
          errorMessage(this.$store, { message: error.message });
        });
    },
  },
};
</script>

<style lang="scss" scoped>
table {
  font-size: 12px;
  .date {
    width: 200px;
  }
}
.refresh-site {
  border: none;
  &:focus {
    border: none;
    box-shadow: none;
  }
  color: silver;
}
.rollback-buttons {
  float: right;
}
</style>
